import React, { useState } from 'react';
import {
  Layout,
  Grid3x3,
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  Maximize,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowLeft,
  Undo2,
  Redo2,
  MousePointer2,
  ZoomIn,
  ZoomOut,
  Lock,
  Unlock,
  Minimize2,
} from 'lucide-react';
import { LayoutDirection } from '../../utils/layoutEngine';
import Tooltip from '../common/Tooltip';

interface LayoutControlsProps {
  onAutoLayout: (direction: LayoutDirection) => void;
  onCompactLayout: () => void;
  onAlignNodes: (alignment: 'left' | 'right' | 'center' | 'top' | 'bottom' | 'middle') => void;
  onDistributeNodes: (direction: 'horizontal' | 'vertical') => void;
  onToggleGrid: () => void;
  onToggleSnapToGrid: () => void;
  onFitView: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onToggleSelectionMode?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onToggleCanvasLock?: () => void;
  zoomLevel?: number;
  onResetZoom?: () => void;
  gridEnabled: boolean;
  snapToGrid: boolean;
  selectionMode?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  isCanvasLocked?: boolean;
  selectedNodesCount: number;
}

const LayoutControls: React.FC<LayoutControlsProps> = ({
  onAutoLayout,
  onCompactLayout,
  onAlignNodes,
  onDistributeNodes,
  onToggleGrid,
  onToggleSnapToGrid,
  onFitView,
  onUndo,
  onRedo,
  onToggleSelectionMode,
  onZoomIn,
  onZoomOut,
  onToggleCanvasLock,
  zoomLevel,
  onResetZoom,
  gridEnabled,
  snapToGrid,
  selectionMode = false,
  canUndo = false,
  canRedo = false,
  isCanvasLocked = false,
  selectedNodesCount,
}) => {
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [showAlignMenu, setShowAlignMenu] = useState(false);

  // AC-1626: clamp the displayed zoom so it can never render "NaN%" /
  // "Infinity%". When the assistant-created canvas produced NaN-positioned
  // nodes, React Flow's fitView yielded a NaN zoom that flowed down as
  // `zoomLevel`. Guard with Number.isFinite and fall back to 1 (=> "100%").
  const displayZoom = Number.isFinite(zoomLevel) ? (zoomLevel as number) : 1;

  const layoutDirections: { label: string; value: LayoutDirection; icon: React.ReactNode }[] = [
    { label: 'Top to Bottom', value: 'TB', icon: <ArrowDown className="w-4 h-4" /> },
    { label: 'Left to Right', value: 'LR', icon: <ArrowRight className="w-4 h-4" /> },
    { label: 'Bottom to Top', value: 'BT', icon: <ArrowUp className="w-4 h-4" /> },
    { label: 'Right to Left', value: 'RL', icon: <ArrowLeft className="w-4 h-4" /> },
  ];

  return (
    <div className="absolute top-4 left-0 z-10">
      <div
        role="toolbar"
        aria-label="Canvas controls"
        className="bg-white dark:bg-cap-dark rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 flex flex-col items-center gap-1 w-fit max-w-[200px]"
      >
        {/* Auto Layout */}
        <div className="relative">
          <button
            onClick={() => setShowLayoutMenu(!showLayoutMenu)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Auto layout"
            aria-haspopup="menu"
            aria-expanded={showLayoutMenu}
          >
            <Layout className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Auto Layout
            </span>
          </button>
          
          {showLayoutMenu && (
            <div className="absolute top-full left-full ml-2 bg-white dark:bg-cap-dark rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 w-[180px] max-w-[calc(100vw-2rem)]">
              {layoutDirections.map((direction) => (
                <button
                  key={direction.value}
                  onClick={() => {
                    onAutoLayout(direction.value);
                    setShowLayoutMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
                >
                  {direction.icon}
                  {direction.label}
                </button>
              ))}
              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              <button
                onClick={() => {
                  onCompactLayout();
                  setShowLayoutMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <Minimize2 className="w-4 h-4" />
                Compact
              </button>
            </div>
          )}
        </div>

        <div className="h-px w-6 bg-gray-300" />

        {/* Alignment Tools */}
        <div className="relative">
          <button
            onClick={() => setShowAlignMenu(!showAlignMenu)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group relative disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            disabled={selectedNodesCount < 2}
            aria-label="Align nodes"
            aria-haspopup="menu"
            aria-expanded={showAlignMenu}
          >
            <AlignCenterVertical className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Align Nodes
            </span>
          </button>
          
          {showAlignMenu && selectedNodesCount >= 2 && (
            <div className="absolute top-full left-full ml-2 bg-white dark:bg-cap-dark rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 w-[220px] max-w-[calc(100vw-2rem)]">
              <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 font-semibold">Horizontal</div>
              <button
                onClick={() => {
                  onAlignNodes('left');
                  setShowAlignMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <AlignStartVertical className="w-4 h-4" />
                Align Left
              </button>
              <button
                onClick={() => {
                  onAlignNodes('center');
                  setShowAlignMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <AlignCenterVertical className="w-4 h-4" />
                Align Center
              </button>
              <button
                onClick={() => {
                  onAlignNodes('right');
                  setShowAlignMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <AlignEndVertical className="w-4 h-4" />
                Align Right
              </button>

              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 font-semibold">Vertical</div>

              <button
                onClick={() => {
                  onAlignNodes('top');
                  setShowAlignMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <AlignStartHorizontal className="w-4 h-4" />
                Align Top
              </button>
              <button
                onClick={() => {
                  onAlignNodes('middle');
                  setShowAlignMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <AlignCenterHorizontal className="w-4 h-4" />
                Align Middle
              </button>
              <button
                onClick={() => {
                  onAlignNodes('bottom');
                  setShowAlignMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
              >
                <AlignEndHorizontal className="w-4 h-4" />
                Align Bottom
              </button>
              
              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 font-semibold">Distribute</div>
              
              <button
                onClick={() => {
                  onDistributeNodes('horizontal');
                  setShowAlignMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-sm whitespace-nowrap"
                disabled={selectedNodesCount < 3}
              >
                Distribute Horizontally
              </button>
              <button
                onClick={() => {
                  onDistributeNodes('vertical');
                  setShowAlignMenu(false);
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                disabled={selectedNodesCount < 3}
              >
                Distribute Vertically
              </button>
            </div>
          )}
        </div>

        <div className="h-px w-6 bg-gray-300" />

        {/* Grid Toggle */}
        <button
          onClick={onToggleGrid}
          className={`p-2 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            gridEnabled ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          aria-label={gridEnabled ? 'Hide grid' : 'Show grid'}
          aria-pressed={gridEnabled}
        >
          <Grid3x3 className={`w-5 h-5 ${gridEnabled ? 'text-primary-600' : 'text-gray-700 dark:text-gray-300'}`} />
          <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {gridEnabled ? 'Hide Grid' : 'Show Grid'}
          </span>
        </button>

        {/* Snap to Grid */}
        <button
          onClick={onToggleSnapToGrid}
          className={`p-2 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            snapToGrid ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          aria-label={snapToGrid ? 'Disable snap to grid' : 'Enable snap to grid'}
          aria-pressed={snapToGrid}
        >
          <div className="relative">
            <Grid3x3 className={`w-5 h-5 ${snapToGrid ? 'text-primary-600' : 'text-gray-700 dark:text-gray-300'}`} />
            {snapToGrid && (
              <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-primary-600 rounded-full" />
            )}
          </div>
          <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {snapToGrid ? 'Disable Snap' : 'Enable Snap'}
          </span>
        </button>

        <div className="h-px w-6 bg-gray-300" />

        {/* Selection Mode */}
        {onToggleSelectionMode && (
          <button
            onClick={onToggleSelectionMode}
            className={`p-2 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              selectionMode ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            aria-label={selectionMode ? 'Exit selection mode' : 'Enter selection mode'}
            aria-pressed={selectionMode}
          >
            <MousePointer2 className={`w-5 h-5 ${selectionMode ? 'text-primary-600' : 'text-gray-700 dark:text-gray-300'}`} />
            <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {selectionMode ? 'Exit Selection Mode' : 'Selection Mode (Box Select)'}
            </span>
          </button>
        )}

        {/* Fit View */}
        <button
          onClick={onFitView}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label="Fit view"
        >
          <Maximize className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Fit View
          </span>
        </button>

        {/* Zoom Controls */}
        {onZoomIn && (
          <button
            onClick={onZoomIn}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Zoom in"
          >
            <ZoomIn className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Zoom In
            </span>
          </button>
        )}

        {/* Zoom Percentage Indicator */}
        {zoomLevel !== undefined && onResetZoom && (
          <button
            onClick={onResetZoom}
            className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-xs font-medium text-gray-700 dark:text-gray-300"
            aria-label="Reset zoom to 100%"
          >
            {Math.round(displayZoom * 100)}%
            <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Reset to 100%
            </span>
          </button>
        )}

        {onZoomOut && (
          <button
            onClick={onZoomOut}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Zoom out"
          >
            <ZoomOut className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Zoom Out
            </span>
          </button>
        )}

        {/* Canvas Lock */}
        {onToggleCanvasLock && (
          <button
            onClick={onToggleCanvasLock}
            className={`p-2 rounded-md transition-colors group relative focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              isCanvasLocked ? 'bg-blue-100 hover:bg-blue-200' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            aria-label={isCanvasLocked ? 'Unlock canvas' : 'Lock canvas'}
            aria-pressed={isCanvasLocked}
          >
            {isCanvasLocked ? (
              <Unlock className={`w-5 h-5 ${isCanvasLocked ? 'text-primary-600' : 'text-gray-700 dark:text-gray-300'}`} />
            ) : (
              <Lock className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            )}
            <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {isCanvasLocked ? 'Unlock Canvas' : 'Lock Canvas'}
            </span>
          </button>
        )}

        {/* Undo/Redo */}
        {(onUndo || onRedo) && (
          <>
            <div className="h-px w-6 bg-gray-300" />
            
            {onUndo && (
              <button
                onClick={onUndo}
                disabled={!canUndo}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group relative disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-label="Undo"
              >
                <Undo2 className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  Undo
                </span>
              </button>
            )}
            
            {onRedo && (
              <button
                onClick={onRedo}
                disabled={!canRedo}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group relative disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-label="Redo"
              >
                <Redo2 className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  Redo
                </span>
              </button>
            )}
          </>
        )}

        {/* Selected nodes indicator */}
        {selectedNodesCount > 0 && (
          <>
            <div className="h-px w-6 bg-gray-300" />
            <div className="relative group">
              <div className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 rounded-md">
                {selectedNodesCount}
              </div>
              <span className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                {selectedNodesCount} node{selectedNodesCount !== 1 ? 's' : ''} selected
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LayoutControls;